/**
 * Разбор результата тренировки.
 *
 * Из потока нажатий формируются агрегаты по учебным единицам: именно они, а не
 * сырые события, уходят на сервер (решение AD-004).
 */

import type {
  ErrorKind,
  KeystrokeEvent,
  MasteryStat,
  MasteryUnit,
  TypingError,
  UnitOutcome,
} from '@type2x/shared-contracts';
import { median, percentile } from '@type2x/typing-engine';
import { applyObservation, scheduleNextReview, type LearningConfig } from '@type2x/learning-engine';
import type { Exercise } from '@type2x/exercise-generator';

/** Наблюдения по одной единице внутри сессии. */
interface Accumulator {
  attempts: number;
  errors: number;
  corrections: number;
  latencies: number[];
}

function emptyAccumulator(): Accumulator {
  return { attempts: 0, errors: 0, corrections: 0, latencies: [] };
}

/**
 * Сопоставляет нажатия элементам упражнения.
 *
 * Элементы разделены пробелами, поэтому позиция в тексте однозначно
 * определяет, к какому элементу относится нажатие.
 */
export function collectUnitOutcomes(
  exercise: Exercise,
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
): UnitOutcome[] {
  const byUnit = new Map<string, Accumulator>();
  const errorPositions = new Set(errors.map((error) => error.position));

  let position = 0;
  for (const item of exercise.items) {
    const length = [...item.text].length;

    if (item.unitId !== null) {
      const accumulator = byUnit.get(item.unitId) ?? emptyAccumulator();
      for (let offset = 0; offset < length; offset += 1) {
        const event = events[position + offset];
        if (event === undefined) continue;
        if (event.interKeyIntervalMs > 0) accumulator.latencies.push(event.interKeyIntervalMs);
        if (errorPositions.has(position + offset)) accumulator.errors += 1;
        if (event.corrected) accumulator.corrections += 1;
      }
      accumulator.attempts += length;
      byUnit.set(item.unitId, accumulator);
    }

    // Плюс один символ на пробел-разделитель.
    position += length + 1;
  }

  return [...byUnit.entries()].map(([unitId, accumulator]) => ({
    unitId: unitId as UnitOutcome['unitId'],
    attempts: accumulator.attempts,
    errors: accumulator.errors,
    medianLatencyMs: median(accumulator.latencies),
    corrections: accumulator.corrections,
  }));
}

/** Обновляет статистику единиц по итогам сессии. */
export function applyOutcomes(
  units: Readonly<Record<string, MasteryUnit>>,
  stats: Readonly<Record<string, MasteryStat>>,
  outcomes: readonly UnitOutcome[],
  config: LearningConfig,
  nowIso: string,
): MasteryStat[] {
  const nowMs = Date.parse(nowIso);

  return outcomes.flatMap((outcome) => {
    const previous = stats[outcome.unitId];
    const unit = units[outcome.unitId];
    if (previous === undefined || unit === undefined) return [];

    const latencies = [outcome.medianLatencyMs];
    const updated = applyObservation(
      previous,
      {
        attempts: outcome.attempts,
        errors: outcome.errors,
        firstTryCorrect: Math.max(outcome.attempts - outcome.errors, 0),
        corrections: outcome.corrections,
        medianLatencyMs: median(latencies),
        p95LatencyMs: percentile(latencies, 0.95),
      },
      config,
      nowIso,
    );

    return [{ ...updated, nextReviewAt: scheduleNextReview(updated, config, nowMs) }];
  });
}

/** Сводка по типам ошибок сессии. */
export function summariseErrors(
  errors: readonly TypingError[],
): { kind: ErrorKind; count: number }[] {
  const counts = new Map<ErrorKind, number>();
  for (const error of errors) {
    counts.set(error.kind, (counts.get(error.kind) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([kind, count]) => ({ kind, count }))
    .sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind));
}
